import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { AGENT_CONTEXTS } from "../apps";
import { panelName, useWorkspaceFiles } from "../chat/useThreadWorkspace";
import { resolveContext, type ContextDocument } from "../contexts/registry";
import type { WorkspaceFile } from "../sandbox/client/api";
import type { AgentState } from "../store/agentSlice";

type State = { agent: AgentState };

export type MentionCandidate =
  | { kind: "thread_file"; id: number; name: string; contentType: string }
  /** A Files panel file (`id` is its path): picking it shares it into the conversation first. */
  | { kind: "workspace_file"; id: string; name: string; workspaceId: string; file: WorkspaceFile }
  /** `contextId` is the context the documents list belongs to (the application). */
  | ({ kind: "document"; contextId: number } & ContextDocument);

const NO_DOCUMENTS: ContextDocument[] = [];

/**
 * What @ offers in the composer: this chat's attachments, then its workspace's Files panel files,
 * then the documents of the context the thread is about (from that context type's `documents`
 * query). H2 draws the autocomplete.
 */
export function useMentionCandidates(threadId: number | null): MentionCandidate[] {
  // Two narrow selections: the thread object changes on every streamed delta, these do not.
  const messages = useSelector((state: State) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.messages
  );
  const contextRef = useSelector((state: State) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.contextRef
  );
  const context = resolveContext(AGENT_CONTEXTS, contextRef);
  const documentsQuery = context?.def.documents?.(context.id) ?? null;
  const { data: documents = NO_DOCUMENTS } = useQuery({
    queryKey: documentsQuery?.queryKey ?? ["agentNoDocuments"],
    queryFn: documentsQuery?.queryFn ?? (async () => null),
    select: documentsQuery?.select ?? (() => NO_DOCUMENTS),
    staleTime: documentsQuery?.staleTime,
    enabled: !!documentsQuery,
  });
  const { workspace, files: workspaceFiles } = useWorkspaceFiles(threadId);
  const workspaceId = workspace?.id ?? null;

  return useMemo(() => {
    const files = new Map<number, { name: string; contentType: string }>();
    for (const message of messages ?? []) {
      for (const part of message.parts) {
        if (part.kind === "file" && part.thread_file_id != null) {
          files.set(part.thread_file_id, { name: part.name, contentType: part.media_type });
        }
      }
    }
    const contextId = context?.id ?? 0;
    return [
      ...[...files].map(([id, file]) => ({ kind: "thread_file" as const, id, ...file })),
      ...(workspaceId == null ? [] : workspaceFiles).map((file) => ({
        kind: "workspace_file" as const,
        id: file.path,
        name: panelName(file.path),
        workspaceId: workspaceId as string,
        file,
      })),
      ...documents.map((document) => ({ kind: "document" as const, contextId, ...document })),
    ];
  }, [messages, workspaceFiles, workspaceId, documents, context?.id]);
}
